import React, { useEffect, useState } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { Badge, Field, FlexWrapper, Text } from '~/components/Experiences/components';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import { ORIGINAL_EXPERIENCE_CONTENT_TAB, themes } from '~/consts/experiences';

import { sortBy } from '~/utils/arrayUtils';

const getHumanizedType = (type: Experiences.BookingField['type']) => {
  switch (type) {
    case 'BIG-TEXT':
      return 'Big text';
    case 'TEXT':
      return 'Short text';
    case 'INTEGER':
      return 'Integer number';
    case 'FLOAT':
      return 'Decimal number';
    case 'BOOLEAN':
      return 'Boolean';
    case 'DATE':
      return 'Date';
    case 'PHONE':
      return 'Phone';
    case 'SIMPLE-SELECT':
      return 'Single option';
    case 'MULTI-SELECT':
      return 'Multi options';
    case 'EMAIL':
      return 'Email';
    default:
      return type;
  }
};

function SortableList({ items, areOriginalValues, onValueChange, onOptionChange, onDragEnd }) {
  return (
    <DndContext onDragEnd={onDragEnd}>
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: '2px' }}>
          {items.map((bookingField) => (
            <SortableItem
              key={bookingField.id}
              bookingField={bookingField}
              onValueChange={onValueChange}
              onOptionChange={onOptionChange}
              areOriginalValues={areOriginalValues}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ bookingField, onOptionChange, onValueChange, areOriginalValues }) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: bookingField.id,
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      mb="12px"
      sx={{
        border: `1px solid ${themes?.disabled}`,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      {/* Card Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px="16px"
        py="10px"
        sx={{
          backgroundColor: themes?.primaryLight,
          borderBottom: `1px solid ${themes?.disabled}`,
        }}
      >
        <Badge color={themes?.primaryDark} title={getHumanizedType(bookingField.type)} />

        <FlexWrapper style={{ width: 'auto', gap: '10px' }}>
          {bookingField.required && <Badge title="Required" background={themes?.primaryRed} />}
          <Text size="12px" color={themes?.secondaryGrey} weight={400}>
            #{bookingField.sort}
          </Text>
          <Box
            {...listeners}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: themes?.primaryGrey }}
          >
            <DragIndicator fontSize="small" />
          </Box>
        </FlexWrapper>
      </Box>

      {/* Card Body */}
      <Box px="16px" py="14px">
        <FlexWrapper style={{ gap: '12px', alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <Field
              style={{ width: '100%' }}
              name="label"
              onChange={({ value }) => onValueChange(bookingField.id, { label: value })}
              labelName="Label"
              value={bookingField.label}
              disabled={areOriginalValues}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Field
              style={{ width: '100%' }}
              name="placeholder"
              onChange={({ value }) => onValueChange(bookingField.id, { placeholder: value })}
              labelName="Placeholder"
              value={bookingField.placeholder}
              disabled={areOriginalValues}
            />
          </Box>
          <Box sx={{ pb: '4px' }}>
            <FormGroup>
              <Tooltip
                title={bookingField.required ? 'This field is required and cannot be removed from checkout' : ''}
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bookingField.showOnCheckout}
                      disabled={areOriginalValues || bookingField.required}
                      onChange={(event) => onValueChange(bookingField.id, { showOnCheckout: event.target.checked })}
                      size="small"
                    />
                  }
                  label={
                    <Text
                      size="13px"
                      weight={400}
                      color={areOriginalValues || bookingField.required ? themes?.primaryGrey : themes?.primaryDark}
                    >
                      Show on checkout
                    </Text>
                  }
                />
              </Tooltip>
            </FormGroup>
          </Box>
        </FlexWrapper>

        {/* Hide for now
        {!!bookingField.options && (
          <Box mt="14px" sx={{ borderTop: `1px solid ${themes?.primaryLight}`, pt: '12px' }}>
            <Text size="12px" color={themes?.secondaryGrey} weight={500} mb="10px">
              Options
            </Text>
            <FlexWrapper style={{ flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
              {bookingField.options.map((option, optionIndex) => (
                <Box key={optionIndex} sx={{ minWidth: '140px', flex: '1 1 140px', maxWidth: '200px' }}>
                  <Field
                    style={{ width: '100%' }}
                    name="option"
                    onChange={({ value }) => onOptionChange(bookingField.id, optionIndex, { label: value })}
                    labelName={option.value}
                    value={option.label}
                    disabled={areOriginalValues}
                  />
                </Box>
              ))}
            </FlexWrapper>
          </Box>
        )} */}
      </Box>
    </Box>
  );
}

function ExperienceCurationBookingFields() {
  const { contentTab, updateValues, values } = useUpdateExperienceValues();
  const [sortedItems, setSortedItems] = useState<Array<Experiences.BookingField>>([]);

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  const handleValue = (id: string, data: Partial<Experiences.BookingField>) => {
    setSortedItems((sortedItems) => {
      const index = sortedItems.findIndex((item) => item.id === id);

      sortedItems[index] = {
        ...sortedItems[index],
        ...data,
      };
      updateValues({
        bookingFields: sortedItems,
      });

      return sortedItems;
    });
  };

  const handleOptions = (id: string, optionIndex, data: Partial<Experiences.BookingField['options'][number]>) => {
    setSortedItems((sortedItems) => {
      const index = sortedItems.findIndex((item) => item.id === id);

      sortedItems[index].options[optionIndex] = {
        ...sortedItems[index].options[optionIndex],
        ...data,
      };

      updateValues({
        bookingFields: sortedItems,
      });

      return sortedItems;
    });
  };

  const handleSort = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active?.id || !over?.id) {
      return;
    }

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);

    const newBookingsFields = arrayMove(sortedItems, oldIndex, newIndex).map((item, index) => ({
      ...item,
      sort: index + 1,
    }));

    setSortedItems(newBookingsFields);
    updateValues({ bookingFields: newBookingsFields });
  };

  useEffect(() => {
    if (values && !sortedItems.length) {
      const sorted = sortBy(values.bookingFields, (item) => item.sort, 'asc');
      setSortedItems(sorted);
    }
  }, [sortedItems.length, values]);

  return (
    <Accordion variant="outlined" disabled={!sortedItems.length}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>TRAVELLER FIELDS</AccordionSummary>
      <AccordionDetails>
        <SortableList
          items={sortedItems}
          areOriginalValues={areOriginalValues}
          onOptionChange={handleOptions}
          onValueChange={handleValue}
          onDragEnd={handleSort}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default ExperienceCurationBookingFields;
